import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Upload, ImageIcon, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TechPanelBranding() {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [branding, setBranding] = useState({
    has_custom_logo: false,
    logo_filename: null,
    logo_updated: null,
    company_name: ''
  });
  const fileInputRef = useRef(null);

  const getAuthConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/tech-branding`, getAuthConfig());
      setBranding(res.data);
    } catch (error) {
      console.error('Failed to fetch branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPG, WEBP, or SVG image');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 2MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/api/settings/tech-logo`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Logo uploaded successfully');
      fetchBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/settings/tech-logo`, getAuthConfig());
      toast.success('Logo removed. Default logo will be used');
      setBranding({ ...branding, has_custom_logo: false, logo_filename: null });
    } catch (error) {
      toast.error('Failed to remove logo');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-theme">
        <CardContent className="py-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-theme" data-testid="tech-branding-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-base sm:text-lg">Technician Panel Branding</CardTitle>
        </div>
        <CardDescription>
          Upload your company logo to display in the technician mobile app instead of the default Fieldz AI logo
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Logo Preview */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-32 h-20 rounded-xl bg-black/50 border border-theme flex items-center justify-center overflow-hidden">
            {branding.has_custom_logo && branding.logo_filename ? (
              <img 
                src={`${API}/uploads/logos/${branding.logo_filename}?t=${Date.now()}`}
                alt="Custom Logo"
                className="max-w-full max-h-full object-contain p-2"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="text-center p-2">
                <img src="/logo.png" alt="Default Logo" className="max-h-12 mx-auto opacity-50" />
                <span className="text-[10px] text-muted-foreground/60 block mt-1">Default</span>
              </div>
            )}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-foreground font-medium text-sm">Current Logo</span>
              {branding.has_custom_logo ? (
                <Badge className="bg-green-500/20 text-green-400 text-xs">Custom</Badge>
              ) : (
                <Badge className="bg-theme-surface text-muted-foreground text-xs">Default</Badge>
              )}
            </div>
            <p className="text-muted-foreground text-xs">
              {branding.has_custom_logo 
                ? `Uploaded: ${new Date(branding.logo_updated).toLocaleDateString()}`
                : 'Using default Fieldz AI logo'
              }
            </p>
          </div>
        </div>

        {/* Upload Section */}
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
              onChange={handleFileSelect}
              className="hidden"
              id="logo-upload"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn-primary"
              data-testid="upload-logo-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  {branding.has_custom_logo ? 'Replace Logo' : 'Upload Logo'}
                </>
              )}
            </Button>
            
            {branding.has_custom_logo && (
              <Button
                onClick={handleDelete}
                disabled={deleting}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                data-testid="remove-logo-btn"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Remove
              </Button>
            )}
          </div>
          
          <p className="text-muted-foreground/60 text-xs">
            Supported formats: PNG, JPG, WEBP, SVG • Max size: 2MB • Recommended: 200x60 pixels
          </p>
        </div>

        {/* Preview Section */}
        <div className="border border-theme rounded-xl p-4 bg-black/30">
          <p className="text-muted-foreground text-xs mb-3">Preview in Technician App:</p>
          <div className="bg-gradient-to-br from-[#0a0a0f] via-[#0d1117] to-[#0a0a0f] rounded-lg p-3 border border-theme">
            <div className="flex items-center justify-between border-b border-theme pb-2 mb-2">
              <div className="w-6 h-4 bg-theme-surface rounded"></div>
              <div className="h-8 flex items-center justify-center">
                {branding.has_custom_logo && branding.logo_filename ? (
                  <img 
                    src={`${API}/uploads/logos/${branding.logo_filename}?t=${Date.now()}`}
                    alt="Logo Preview"
                    className="max-h-8 object-contain"
                  />
                ) : (
                  <img src="/logo.png" alt="Default" className="h-6" />
                )}
              </div>
              <div className="w-6 h-4 bg-theme-surface rounded"></div>
            </div>
            <div className="space-y-2">
              <div className="h-3 bg-theme-surface rounded w-3/4"></div>
              <div className="h-3 bg-theme-surface rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
